import { createContext, useContext, useState, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useApi } from '@/api/useApi.tsx';
import ExceptionManager from '@/managers/exception-manager.tsx';
import { ExpertQuestionCreateRequest, PassedQuestionCreateRequest } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { toast } from 'react-toastify';
import { useSolverTimer } from '@/hooks/use-solver-timer.tsx';
import { ReportedQuestionCreateRequest } from '@dopingtech/live_expert_bff_api_ts';
import NotificationManager from '@/managers/notification-manager.tsx';
import { UseMutationResult } from 'react-query';
import { TimeSpan } from '@/utility/time-span.ts';
import { useFeatureFlag } from 'configcat-react';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import { jsonTryParse, jsonTryStringify } from '@/utility';
import { LocalStorageKeys } from '@/constants/local-storage-keys.ts';
import { PostMessage } from '@/constants/post-message.ts';
import { isWebView } from '@/utility/webview.ts';

export enum CallState {
	DEFAULT = 'DEFAULT',
	QUESTIONS = 'QUESTIONS',
	PASSED_QUESTIONS = 'PASSED_QUESTIONS'
}

interface ModalContent {
	title: string;
	message: string;
	approveButtonText: string;
	rejectButtonText: string;
	approveButtonFunc: () => void;
	rejectButtonFunc: () => void;
	approveButtonIcon?: ReactNode;
	rejectButtonIcon?: ReactNode;
}

export interface QuestionData {
	interactionId: string;
	questionId: string;
	curriculumName: string;
	curriculumElementUnitName: string;
	questionImage: string;
	currentAssignedQuestion?: boolean;
	questionCreatedAt?: string;
	timeoutAt?: string;
}

interface ReportReasons {
	id: string;
	description: string;
}

interface ResolveQuestionNavState {
	id: number;
	questionId: number;
	interactionId?: string;
	questionImage?: string;
	curriculumName?: string;
	curriculumElementUnitName?: string;
}

interface SolveQuestionContextType {
	callState: CallState;
	setCallState: (state: CallState) => void;
	modalContent: ModalContent | null;
	setModalContent: (content: ModalContent | null) => void;
	isActiveQuestionInspect: boolean;
	setIsActiveQuestionInspect: (active: boolean) => void;
	isDisabledQuestionTimer: boolean;
	setIsDisabledQuestionTimer: (disabled: boolean) => void;
	questionIsAccepted: boolean;
	setQuestionIsAccepted: (accepted: boolean) => void;
	isOpenSolveBottomSheet: boolean;
	setIsOpenSolveBottomSheet: (open: boolean) => void;
	isActiveQuestionReport: boolean;
	setIsActiveQuestionReport: (active: boolean) => void;
	activeSlideIndex: number;
	setActiveSlideIndex: (index: number) => void;
	selectedSolveType: string | null;
	setSelectedSolveType: (type: string | null) => void;
	setIsOpenImageCropModal: (open: boolean) => void;
	isOpenImageCropModal: boolean;
	setIsOpenDirectSendQuestion: (open: boolean) => void;
	isOpenDirectSendQuestion: boolean;
	_isActiveSolverMobileDirection: boolean;
	_isActiveSolverWebDirection: boolean;

	onOpenPassedQuestions: () => void;
	onClosePassedQuestions: () => void;
	isActivePassedQuestions: boolean;
	isNullablePassedQuestions: boolean;
	setIsNullablePassedQuestions: void;

	isResolveMode: boolean;
	resolveQueueId: number | null;

	questionPool: QuestionData[] | undefined;
	passedQuestionPool: QuestionData[] | undefined;
	isQuestionPoolLoading: boolean;
	questionPoolError: any;

	handleStartSolving: (questionId: string, interactionId: string) => Promise<void>;
	handleRejectQuestion: (questionId: string, isTimeout?: boolean) => Promise<void>;
	refetchQuestionPool: () => void;
	moveToNextQuestion: (startTimer?: boolean) => void;

	isCreatingExpertQuestion: boolean;
	isCreatingPassedQuestion: boolean;

	getActiveQuestion: () => QuestionData | undefined;
	hasQuestions: boolean;

	activeTimer: ReturnType<typeof useSolverTimer>;

	remainingPassedQuestionCount: number;
	currentQuestionTimer: number;
	setCurrentQuestionTimer: (remainingTime: number) => void;
	updateCurrentQuestionTimer: (remainingTime: number) => void;

	allReportReasons: ReportReasons[];
	createReportedQuestion: UseMutationResult<unknown, unknown, ReportedQuestionCreateRequest, unknown> & {
		safeMutate: (variables: ReportedQuestionCreateRequest) => Promise<unknown>;
	};
}

const SolveQuestionContext = createContext<SolveQuestionContextType | undefined>(undefined);

interface SolveQuestionProviderProps {
	children: ReactNode;
}

export const QUESTION_ACCEPT_DURATION = 45;

const isDesktop = !isWebView();

export const SolveQuestionProvider = ({ children }: SolveQuestionProviderProps) => {
	const { SolverQuestionControllerApi, ReportedQuestionControllerApi, ReportReasonControllerApi } = useApi();

	const location = useLocation();
	const resolveQuestionFromState = (location.state as { resolveQuestion?: ResolveQuestionNavState } | null)?.resolveQuestion;

	const [callState, setCallState] = useState<CallState>(resolveQuestionFromState ? CallState.QUESTIONS : CallState.DEFAULT);
	const [modalContent, setModalContent] = useState<ModalContent | null>(null);
	const [isActiveQuestionInspect, setIsActiveQuestionInspect] = useState(false);
	const [isDisabledQuestionTimer, setIsDisabledQuestionTimer] = useState(!!resolveQuestionFromState);
	const [questionIsAccepted, setQuestionIsAccepted] = useState(!!resolveQuestionFromState);
	const [resolveQueueId, setResolveQueueId] = useState<number | null>(resolveQuestionFromState?.id ?? null);
	const [isOpenSolveBottomSheet, setIsOpenSolveBottomSheet] = useState(false);
	const [isOpenImageCropModal, setIsOpenImageCropModal] = useState(false);
	const [isActiveQuestionReport, setIsActiveQuestionReport] = useState(false);
	const [isOpenDirectSendQuestion, setIsOpenDirectSendQuestion] = useState(false);
	const [isActivePassedQuestions, setIsActivePassedQuestions] = useState(false);
	const [isNullablePassedQuestions, setIsNullablePassedQuestions] = useState(false);
	const [activeSlideIndex, setActiveSlideIndex] = useState(0);
	const [selectedSolveType, setSelectedSolveType] = useState<string | null>(null);

	const [questionPool, setQuestionPool] = useState<QuestionData[] | undefined>(() =>
		resolveQuestionFromState
			? [
					{
						interactionId: resolveQuestionFromState.interactionId ?? String(resolveQuestionFromState.questionId),
						questionId: String(resolveQuestionFromState.questionId),
						curriculumName: resolveQuestionFromState.curriculumName ?? '',
						curriculumElementUnitName: resolveQuestionFromState.curriculumElementUnitName ?? '',
						questionImage: resolveQuestionFromState.questionImage ?? ''
					}
				]
			: undefined
	);

	const [passedQuestionPool, setPassedQuestionPool] = useState<QuestionData[] | undefined>(undefined);

	const [currentQuestionTimer, setCurrentQuestionTimer] = useState<number>(QUESTION_ACCEPT_DURATION);

	const { value: isActiveSolverMobileDirection } = useFeatureFlag(CONFIGCAT_KEYS.IS_ACTIVE_SOLVER_MOBILE_DIRECTION, false);

	const { value: isActiveSolverWebDirection, loading: isLoadingIsActiveSolverWebDirection } = useFeatureFlag(CONFIGCAT_KEYS.IS_ACTIVE_SOLVER_WEB_DIRECTION, false);

	const _isActiveSolverMobileDirection = useMemo(() => isActiveSolverMobileDirection && isDesktop, [isDesktop, isActiveSolverMobileDirection]);

	const _isActiveSolverWebDirection = useMemo(() => isActiveSolverWebDirection && !isDesktop, [isDesktop, isActiveSolverMobileDirection, isLoadingIsActiveSolverWebDirection]);

	const {
		data: remainingPassedQuestionCount,
		isLoading: isRemainingPassedQuestionCountLoading,
		refetch: refetchRemainingPassedQuestionCount
	} = useQueryProvider([QUERY_KEYS.getRemainingPassedQuestionCount], () => SolverQuestionControllerApi.getRemainingPassedQuestionCount(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		onSuccess: remainingPassedCount => {
			if (remainingPassedCount === 0 && !resolveQueueId) {
				setCallState(CallState.PASSED_QUESTIONS);
			}
		}
	});

	const { data: allReportReasons } = useQueryProvider([QUERY_KEYS.getAllReportReasons], () => ReportReasonControllerApi.getAllReportReasons(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		staleTime: Infinity,
		cacheTime: Infinity,
		enabled: isActiveQuestionReport
	});

	const getActiveQuestion = useCallback((): QuestionData | undefined => {
		const currentPool = isActivePassedQuestions ? passedQuestionPool : questionPool;

		if (!currentPool || currentPool?.length === 0) return undefined;

		const safeIndex = Math.min(activeSlideIndex, currentPool?.length - 1);

		return currentPool[safeIndex];
	}, [questionPool, activeSlideIndex, passedQuestionPool, isActivePassedQuestions]);

	const activeTimer = useSolverTimer(getActiveQuestion()?.interactionId);

	const {
		isLoading: isQuestionPoolLoading,
		error: questionPoolError,
		refetch: refetchQuestionPool
	} = useQueryProvider(
		[QUERY_KEYS.getExpertQuestionPool, `isActivePassedQuestions:${isActivePassedQuestions}`, `CallState.PASSED_QUESTIONS:${callState === CallState.PASSED_QUESTIONS}`],
		() => SolverQuestionControllerApi.getExpertQuestionPool(callState === CallState.PASSED_QUESTIONS || isActivePassedQuestions),
		{
			select: ({ data }) => data.data?.content,
			refetchOnWindowFocus: false,
			enabled: !isRemainingPassedQuestionCountLoading && !resolveQueueId,
			onSuccess: data => {
				if (!isActivePassedQuestions) {
					setQuestionPool(data);
				} else {
					setPassedQuestionPool(data);
				}

				if (data?.[0]?.currentAssignedQuestion) {
					setCallState(CallState.QUESTIONS);
					setQuestionIsAccepted(true);

					const currentQuestion = data[0];

					if (currentQuestion.questionCreatedAt && currentQuestion.timeoutAt) {
						const timerStarted = activeTimer.startTimer(currentQuestion.questionCreatedAt, currentQuestion.timeoutAt);

						if (timerStarted) {
							setIsDisabledQuestionTimer(true);
						}
					}
				}

				if (isActivePassedQuestions && !data?.length) {
					setIsNullablePassedQuestions(true);
					setIsActivePassedQuestions(false);
					toast.info('Pas geçtiğiniz sorular diğer uzmanlarımız tarafından çözüldü. Yeni gelen sorular arasından seçim yaparak çözmeye devam edebilirsiniz.');
				}
			}
		}
	);

	useEffect(() => {
		if (window?.ReactNativeWebView?.postMessage) {
			window.handleWebViewMessage = (message: any) => {
				if (message.type === PostMessage.VIDEO_SENT_SUCCESS) {
					setCallState(CallState.DEFAULT);
					setQuestionIsAccepted(false);
					setIsDisabledQuestionTimer(false);
					setActiveSlideIndex(0);
					refetchQuestionPool();
					toast.success('Video çözümün başarıyla gönderildi.');
				} else if (message.type === PostMessage.VIDEO_TIMEOUT) {
					refetchQuestionPool();
					setCallState(CallState.DEFAULT);
					setQuestionIsAccepted(false);
					setIsDisabledQuestionTimer(false);
					setActiveSlideIndex(0);
				}
			};
		}
	}, []);

	useEffect(() => {
		const activeQuestion = getActiveQuestion();
		if (activeQuestion?.currentAssignedQuestion && activeQuestion.questionCreatedAt && activeQuestion.timeoutAt) {
			if (!activeTimer.isActive) {
				const timerStarted = activeTimer.startTimer(activeQuestion.questionCreatedAt, activeQuestion.timeoutAt);

				if (timerStarted) {
					setIsDisabledQuestionTimer(true);
					setQuestionIsAccepted(true);
				}
			}
		}
	}, [activeTimer, getActiveQuestion]);

	const createExpertQuestion = useMutationProvider((body: ExpertQuestionCreateRequest) => SolverQuestionControllerApi.createExpertQuestion(body), {
		onError: error => {
			ExceptionManager.errorHandler(error);
			setQuestionIsAccepted(false);
			setCallState(CallState.DEFAULT);
			refetchRemainingPassedQuestionCount();
			refetchQuestionPool();
		},
		onSuccess: ({ data }) => {
			if (!data?.data?.createdAt || !data?.data?.timeoutAt) {
				toast.error('Soru bilgileri eksik!');
				return;
			}

			const timerStarted = activeTimer.startTimer(data.data.createdAt, data.data.timeoutAt);

			if (timerStarted) {
				setQuestionIsAccepted(true);
				setIsDisabledQuestionTimer(true);
			}
		}
	});

	const createReportedQuestion = useMutationProvider((body: ReportedQuestionCreateRequest) => ReportedQuestionControllerApi.createReportedQuestion(body), {
		onError: error => {
			ExceptionManager.errorHandler(error);
		},
		onSuccess: () => {
			NotificationManager.notifySolverQuestionReportSuccess();
			setIsActiveQuestionReport(false);
			setIsDisabledQuestionTimer(false);
			moveToNextQuestion();
		}
	});

	const createPassedQuestion = useMutationProvider((body: PassedQuestionCreateRequest) => SolverQuestionControllerApi.createPassedQuestion(body), {
		onError: error => {
			ExceptionManager.errorHandler(error);
		},
		onSuccess: () => {
			refetchRemainingPassedQuestionCount();
		}
	});

	const hasQuestions = Boolean(questionPool && questionPool.length > 0);

	const handleStartSolving = useCallback(
		async (questionId: string) => {
			try {
				activeTimer.clearTimer();
				localStorage.removeItem(LocalStorageKeys.ACTIVE_QUESTION_TIMER);
				localStorage.removeItem(LocalStorageKeys.SOLVER_TIMERS);
				await createExpertQuestion.mutateAsync({ questionId });
			} catch (error) {
				console.error('Soru kabul etme hatası:', error);
			}
		},
		[createExpertQuestion]
	);

	const onOpenPassedQuestions = () => {
		setIsActivePassedQuestions(true);
	};

	const onClosePassedQuestions = () => {
		setIsActivePassedQuestions(false);
	};

	const handleRejectQuestion = useCallback(
		async (questionId: string, isTimeout: boolean = false) => {
			if (!questionId) {
				console.error('Question ID bulunamadı');
				return;
			}

			try {
				await createPassedQuestion.mutateAsync({ questionId });

				if (remainingPassedQuestionCount) {
					activeTimer.clearTimer();
					localStorage.removeItem(LocalStorageKeys.ACTIVE_QUESTION_TIMER);
					localStorage.removeItem(LocalStorageKeys.SOLVER_TIMERS);
				}

				if (!isTimeout) {
					await moveToNextQuestion();
				}
			} catch (error) {
				console.error('Soru reddetme hatası:', error);
			}
		},
		[createPassedQuestion]
	);

	useEffect(() => {
		if (remainingPassedQuestionCount) {
			const timerKey = LocalStorageKeys.ACTIVE_QUESTION_TIMER;

			const interval = setInterval(() => {
				const saved = localStorage.getItem(timerKey);
				const _saved = jsonTryParse(saved);

				if (_saved) {
					const timeoutAt = new Date(_saved?.timeoutAt);
					const now = new Date();
					const remainingSeconds = Math.max(0, Math.floor((timeoutAt - now) / 1000));

					if (remainingSeconds === 0 && callState === CallState.DEFAULT) {
						toast.error('Verilen süre içerisinde seçim yapmadığınız için pas hakkınızdan düşülmüştür.');
						localStorage.removeItem(timerKey);
						clearInterval(interval);
						handleRejectQuestion(_saved.questionId, callState !== CallState.DEFAULT);
					}
				} else {
					clearInterval(interval);
				}
			}, TimeSpan.milliSecond.oneThousand);

			return () => {
				clearInterval(interval);
			};
		}
	}, [activeSlideIndex, questionPool, callState, remainingPassedQuestionCount]);

	useEffect(() => {
		return () => {
			setIsNullablePassedQuestions(false);
		};
	}, []);

	const updateCurrentQuestionTimer = (remainingTime: number) => {
		const activeQuestion = getActiveQuestion();
		if (activeQuestion?.questionId) {
			setCurrentQuestionTimer(remainingTime);
		}

		if (!remainingTime) {
			localStorage.removeItem(LocalStorageKeys.ACTIVE_QUESTION_TIMER);
			localStorage.removeItem(LocalStorageKeys.SOLVER_TIMERS);
		}
	};

	const moveToNextQuestion = useCallback(
		async (startTimer = true) => {
			if (isNullablePassedQuestions) {
				setIsNullablePassedQuestions(false);
			}

			activeTimer.clearTimer();
			localStorage.removeItem(LocalStorageKeys.ACTIVE_QUESTION_TIMER);
			localStorage.removeItem(LocalStorageKeys.SOLVER_TIMERS);

			const nextIndex = activeSlideIndex + 1;

			if (questionPool && nextIndex < questionPool.length && startTimer) {
				if (callState === CallState.QUESTIONS && remainingPassedQuestionCount) {
					const now = new Date();
					const questionTimeoutAt = new Date(now.getTime() + 45 * 1000);

					const activeQuestionTimer = {
						questionId: questionPool[nextIndex].questionId,
						timeoutAt: questionTimeoutAt.toISOString()
					};

					localStorage.setItem(LocalStorageKeys.ACTIVE_QUESTION_TIMER, jsonTryStringify(activeQuestionTimer)!);

					setCurrentQuestionTimer(QUESTION_ACCEPT_DURATION);
				}
				setActiveSlideIndex(nextIndex);
				setQuestionIsAccepted(false);
				setIsDisabledQuestionTimer(false);

				const nextQuestion = questionPool[nextIndex];

				if (nextQuestion?.currentAssignedQuestion && nextQuestion.questionCreatedAt && nextQuestion.timeoutAt) {
					setTimeout(() => {
						const timerStarted = activeTimer.startTimer(nextQuestion.questionCreatedAt!, nextQuestion.timeoutAt!);

						if (timerStarted) {
							setQuestionIsAccepted(true);
							setIsDisabledQuestionTimer(true);
						}
					}, TimeSpan.milliSecond.oneHundred);
				}
			} else {
				setCallState(CallState.DEFAULT);
				setQuestionIsAccepted(false);
				setIsDisabledQuestionTimer(false);
				setActiveSlideIndex(0);
				setCurrentQuestionTimer(QUESTION_ACCEPT_DURATION);
				setResolveQueueId(null);
				await refetchQuestionPool();
			}
		},
		[activeSlideIndex, questionPool, activeTimer, setQuestionIsAccepted, setIsDisabledQuestionTimer, setCallState, setActiveSlideIndex, refetchQuestionPool]
	);

	const contextValue: SolveQuestionContextType = {
		callState,
		setCallState,
		modalContent,
		setModalContent,
		isActiveQuestionInspect,
		setIsActiveQuestionInspect,
		isDisabledQuestionTimer,
		setIsDisabledQuestionTimer,
		questionIsAccepted,
		setQuestionIsAccepted,
		isOpenSolveBottomSheet,
		setIsOpenSolveBottomSheet,
		isActiveQuestionReport,
		setIsActiveQuestionReport,
		activeSlideIndex,
		setActiveSlideIndex,
		selectedSolveType,
		setSelectedSolveType,
		setIsOpenImageCropModal,
		isOpenImageCropModal,
		setIsOpenDirectSendQuestion,
		isOpenDirectSendQuestion,

		questionPool,
		passedQuestionPool,
		isQuestionPoolLoading,
		questionPoolError,

		handleStartSolving,
		handleRejectQuestion,
		refetchQuestionPool,
		moveToNextQuestion,

		isCreatingExpertQuestion: createExpertQuestion.isLoading,
		isCreatingPassedQuestion: createPassedQuestion.isLoading,

		getActiveQuestion,
		hasQuestions,

		activeTimer,

		remainingPassedQuestionCount,

		allReportReasons,
		createReportedQuestion,

		_isActiveSolverMobileDirection,
		_isActiveSolverWebDirection,

		currentQuestionTimer,
		setCurrentQuestionTimer,
		updateCurrentQuestionTimer,

		isActivePassedQuestions,
		onOpenPassedQuestions,
		onClosePassedQuestions,

		isNullablePassedQuestions,
		setIsNullablePassedQuestions,

		isResolveMode: resolveQueueId !== null,
		resolveQueueId
	};

	return <SolveQuestionContext.Provider value={contextValue}>{children}</SolveQuestionContext.Provider>;
};

export const useSolveQuestion = (): SolveQuestionContextType => {
	const context = useContext(SolveQuestionContext);
	if (context === undefined) {
		throw new Error('useSolveQuestion must be used within a SolveQuestionProvider');
	}
	return context;
};
